import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

/* eslint-disable no-restricted-imports */
// Exception: Import from inside of module is needed to remove error in tests
// "TypeError: Cannot read properties of undefined (reading 'injectEndpoints')".
import {
  logout,
  selectAccessToken,
  selectRefreshToken,
  setAccessTokenData,
} from "modules/auth/store/auth.slice";
import { LoginApiResult, prepareTokenData } from "modules/auth/store/utils";
/* eslint-enable no-restricted-imports */
import { clearGlobalFilteredCustomer, selectEnvironment } from "store/appSlice";
import { paramsSerializer } from "utils/paramsSerializer";

import {
  ENV_PRODUCTION,
  SERVICE_API,
  TYPE_ENV_PRODUCTION,
  TYPE_ENV_SANDBOX,
} from "../constants/environment";
import { resolveApiUrl } from "./helpers/resolveApiUrl";

import type { API_SERVICE } from "constants/environment";
import type { RootState } from "store";

export const TAGS = {
  ACCOUNTS: "Accounts",
  BANKS_AUTOCOMPLETE: "BanksAutocomplete",
  CASH_MANAGEMENT_ACCOUNTS: "CashManagementAccounts",
  CIRCUIT_BREAKER_ALLOW_RULES: "CircuitBreakerAllowRules",
  CLIENT: "Client",
  CLIENTS: "Clients",
  CLIENTS_AUTOCOMPLETE: "ClientsAutocomplete",
  CLIENT_NOTIFICATION_SETTINGS: "ClientNotificationSettings",
  CLIENT_NOTIFICATION_OUTSTANDING: "ClientNotificationOutstanding",
  COUNTRIES: "Countries",
  CUSTOMERS: "Customers",
  CUSTOMER: "Customer",
  CUSTOMER_HIERARCHY: "CustomerHierarchy",
  CUSTOMER_AUTOCOMPLETE: "CustomersAutocomplete",
  CUSTOMER_COUNTRIES: "CustomerCountries",
  CUSTOMER_NAMES: "CustomerNames",
  CUSTOMER_PAYMENTS_PROVIDERS: "CustomerPaymentsProviders",
  DYNAMIC_BENEFICIARIES: "DynamicBeneficiaries",
  PAYMENT: "Payment",
  PAYMENTS_PROVIDERS: "PaymentsProviders",
  REFUND_STATUS: "RefundStatus",
  REFUNDS: "Refunds",
  SETTLEMENT_PREFERENCES: "SettlementPreferences",
  USERS: "Users",
  USER_NAMES: "UserNames",
} as const;

const entryPointBaseQuery = fetchBaseQuery({
  baseUrl: resolveApiUrl(ENV_PRODUCTION),
  paramsSerializer,
});

type ExtraOptions = {
  service?: API_SERVICE;
  env?: TYPE_ENV_SANDBOX | TYPE_ENV_PRODUCTION;
};

const environmentAwareBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions
> = async (args, api, extraOptions) => {
  const { service = SERVICE_API, env } = extraOptions ?? {};
  const environment = selectEnvironment(api.getState() as RootState);

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: resolveApiUrl(env ?? environment, service),
    prepareHeaders: (headers, { getState }) => {
      const token = selectAccessToken(getState() as RootState);

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      return headers;
    },
    paramsSerializer,
  });

  return rawBaseQuery(args, api, extraOptions);
};

function isLoginApiResult(data: unknown): data is LoginApiResult {
  return (
    typeof data === "object" &&
    data !== null &&
    data.hasOwnProperty("access_token")
  );
}

const unauthorizedMutex = new Mutex();

const authAwareBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions
> = async (args, api, extraOptions) => {
  await unauthorizedMutex.waitForUnlock();

  let result = await environmentAwareBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) {
    return result;
  }

  if (unauthorizedMutex.isLocked()) {
    await unauthorizedMutex.waitForUnlock();

    return await environmentAwareBaseQuery(args, api, extraOptions);
  }

  const release = await unauthorizedMutex.acquire();
  const refresh_token = selectRefreshToken(api.getState() as RootState);

  if (!refresh_token) {
    api.dispatch(logout());

    return result;
  }

  try {
    const refreshResult = await entryPointBaseQuery(
      { url: "/token/refresh", method: "POST", body: { refresh_token } },
      api,
      extraOptions
    );

    if (isLoginApiResult(refreshResult.data)) {
      const tokenData = prepareTokenData(refreshResult.data);

      api.dispatch(clearGlobalFilteredCustomer());
      api.dispatch(setAccessTokenData(tokenData));
      result = await environmentAwareBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  } finally {
    release();
  }

  return result;
};

export const portalApi = createApi({
  reducerPath: "portalApi",
  baseQuery: authAwareBaseQuery,
  endpoints: () => ({}),
  tagTypes: [...Object.values(TAGS)],
});

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: entryPointBaseQuery,
  endpoints: () => ({}),
});

export interface ApiResponseError {
  status: number;
}

export function isApiResponseError(data: unknown): data is ApiResponseError {
  return typeof data === "object" && data !== null && "status" in data;
}
